/**
 * lib/studentPhotoPolicy.ts
 * ------------------------------------------------------------------
 * PHOTOGRAPHS OF CHILDREN: who may add one, what happens to it, and who
 * may take it down.
 *
 * Parents upload a photo of their own child from the portal and it goes
 * live straight away — the school's choice. Going live immediately is only
 * safe because nothing a parent sends is ever stored as they sent it:
 *
 *   IT LANDS IN A HOLDING AREA the parent can write to and nobody can read.
 *   A server function picks it up within seconds, and only what the server
 *   makes of it reaches the pupil's record.
 *
 *   THE SERVER RE-DRAWS THE IMAGE. A phone photograph carries the GPS
 *   coordinates of where it was taken — very often the family's home. It
 *   also carries whatever else the camera wrote, and a file that merely
 *   claims to be a JPEG can be anything. Re-drawing keeps the pixels and
 *   nothing else, and a file that is not really an image fails to draw.
 *
 *   NOTHING IS SERVED BY A SHAREABLE LINK. The earlier photo code saved a
 *   "download URL" for each photo — a link that opens the image for anybody
 *   who holds it, signed in or not, for good. Forwarded once, a child's
 *   photograph is public. Photos are now fetched through the storage rules
 *   on every view, so only staff and that child's own parents ever see one.
 *   No photo had been uploaded when this changed, so none was ever exposed.
 *
 *   A PARENT NEVER WRITES TO THE PUPIL'S RECORD. The server does, which
 *   keeps the pupil record's already-wide write access from growing.
 *
 * WHO MAY TAKE ONE DOWN: the admissions office and senior staff for every
 * pupil, and a class teacher for their own class — the person most likely
 * to notice that the photo is of the wrong child.
 *
 * Mirrored by hand in functions/lib/studentPhotoPolicy.js; change both.
 * ------------------------------------------------------------------ */

export const PHOTO_UPLOAD_ROOT = 'studentPhotoUploads';
export const PHOTO_LIVE_ROOT = 'studentPhotos';
export const PHOTO_EVENTS_COLLECTION = 'studentPhotoEvents';
export const PHOTO_UPLOAD_STATUS_COLLECTION = 'studentPhotoUploadStatus';

/**
 * Bump when the wording changes, so every recorded consent says which
 * words the parent agreed to.
 */
export const PHOTO_CONSENT_VERSION = 'v1';
export const PHOTO_CONSENT_TEXT =
  "I am this child's parent or guardian. This is a recent photograph of them, "
  + 'and I agree to Livingstone Kolobeng College keeping it on their school record.';

/** What the storage rules accept from a browser. The browser converts
 *  whatever it can read to JPEG first, so in practice it is always JPEG. */
export const ACCEPTED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Sent before upload: long enough for a crisp 800px photo, small enough
 *  for a parent on mobile data. Not a safety measure — the server re-draws
 *  everything regardless. */
export const CLIENT_MAX_EDGE = 2000;

export type PhotoSource = 'parent' | 'staff';

/** The `photo` field on a pupil's record. Written only by the server. */
export interface StudentPhoto {
  path: string;
  thumbPath: string;
  /** Changes on every new photo, so a cached old image is never shown. */
  version: number;
  updatedAt: string;
  source: PhotoSource;
  uploadedBy: string;
  uploadedByName?: string;
  consentVersion?: string;
}

export type PhotoEventAction = 'uploaded' | 'removed' | 'rejected';

export interface PhotoEvent {
  id: string;
  studentId: string;
  studentName?: string;
  classId?: string;
  action: PhotoEventAction;
  source?: PhotoSource;
  by: string;
  byName?: string;
  at: string;
  reason?: string;
  consentVersion?: string;
}

export function uploadPath(studentId: string, uploadId: string): string {
  return `${PHOTO_UPLOAD_ROOT}/${studentId}/${uploadId}`;
}

export function livePaths(studentId: string): { photo: string; thumb: string } {
  return {
    photo: `${PHOTO_LIVE_ROOT}/${studentId}/photo.jpg`,
    thumb: `${PHOTO_LIVE_ROOT}/${studentId}/thumbnail.jpg`,
  };
}

/**
 * Plain advice on a chosen photo's shape and size, shown with the "how it
 * will look" preview. Advice, not a refusal: the photo can still be sent.
 * Every place the portal shows a photo cuts a square from its middle, and
 * the thumbnail the server stores is 160px, so a wide photo loses its
 * sides and a small one looks soft.
 */
export function framingAdvice(width: number, height: number): string[] {
  const out: string[] = [];
  if (!(width > 0) || !(height > 0)) return out;
  if (Math.min(width, height) < 300) {
    out.push('This photo is quite small, so it may look blurry. A larger, clearer photo works better.');
  }
  if (width / height > 1.3) {
    out.push('This photo is wide, so its sides will be cut off. A portrait photo (taller than it is wide) works better.');
  } else if (height / width > 2) {
    out.push('This photo is very tall, so its top and bottom will be cut off.');
  }
  return out;
}

/** Why this file cannot be sent, or null. Said before upload so a parent
 *  is not left watching a spinner for a file the rules will refuse. */
export function uploadFileProblem(f: { type?: string; size?: number } | null | undefined): string | null {
  if (!f) return 'Choose a photo first.';
  if (!String(f.type ?? '').startsWith('image/')) return 'That is not a photo. Choose a JPG or PNG image.';
  if (!(Number(f.size) > 0)) return 'That file is empty.';
  if (Number(f.size) > MAX_UPLOAD_BYTES) return 'That photo is larger than 10 MB. Choose a smaller one.';
  return null;
}

/* ===================== who may take a photo down =================== */

/** The office: sees every upload in Photo Management. */
export const PHOTO_ADMIN_ROLES = [
  'admin', 'systemAdmin', 'headteacher', 'academicAdmin', 'superAcademicAdmin', 'admissions', 'operationsAdmin',
] as const;

/** Who may take a photo down — admin and the academic admins (the school's
 *  decision, Oct 2026). Until then the whole office and class teachers (for
 *  their own class) could; they still see the uploads, and can raise a
 *  wrong photo with an academic admin. */
export const PHOTO_REMOVER_ROLES = [
  'admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin',
] as const;

/** Staff who may upload a photo themselves — unchanged from before. */
export const STAFF_PHOTO_WRITER_ROLES = [
  'admin', 'systemAdmin', 'academicAdmin', 'headteacher',
  'admissions', 'editor', 'classTeacher', 'hoy', 'hod',
] as const;

export interface TeacherLike { id: string; classTeacherOf?: string }
export interface ClassLike { id: string; classTeacherId?: string; assistantClassTeacherId?: string }

/** Is this teacher the class teacher (or assistant) of this class? Both
 *  places the school records it are checked, as permissions.ts does. */
export function teachesClass(
  teacher: TeacherLike | undefined,
  classId: string | undefined,
  classes: ClassLike[],
): boolean {
  if (!teacher || !classId) return false;
  if (teacher.classTeacherOf === classId) return true;
  const cls = classes.find((c) => c.id === classId);
  return Boolean(cls && (cls.classTeacherId === teacher.id || cls.assistantClassTeacherId === teacher.id));
}

export function canRemovePhoto(args: { roles: string[] }): boolean {
  return args.roles.some((r) => (PHOTO_REMOVER_ROLES as readonly string[]).includes(r));
}

/** The uploads a person should see in the review list: everything for the
 *  office, their own class for a class teacher, nothing for anyone else. */
export function visibleEvents(
  events: PhotoEvent[],
  args: { roles: string[]; teacher?: TeacherLike; classes: ClassLike[] },
): PhotoEvent[] {
  if (args.roles.some((r) => (PHOTO_ADMIN_ROLES as readonly string[]).includes(r))) return events;
  if (!args.roles.includes('classTeacher')) return [];
  return events.filter((e) => teachesClass(args.teacher, e.classId, args.classes));
}
